'use client';

import { c as _c } from "react/compiler-runtime";
import { type Context, createContext, type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasAutoshipInCart } from '@libs/common-next/utils';
import type { ItemUI } from '@libs/common-node';
import { isLocaleCA } from '@libs/core';
import { useAutoshipDecisions, useCart } from '../hooks';
import { getENV } from '../utils/env';
import { applyPercentageToEligibleCartItemsState, mergeCrawlAutoshipPercentagesState } from './utils/autoshipPercentageState';
interface AutoshipPercentageProps {
  readonly bannerPercentage: string;
  readonly itemPercentages: Record<string, string>;
  readonly isBannerVisible: boolean;
  readonly hideBanner: () => void;
}
type AutoshipPercentageContextValue = Nullable<AutoshipPercentageProps>;
type AutoshipPercentageGlobal = typeof globalThis & {
  __petmAutoshipPercentageContext?: Context<AutoshipPercentageContextValue>;
};
function getAutoshipPercentageContext() {
  const globalContext = globalThis as AutoshipPercentageGlobal;
  globalContext.__petmAutoshipPercentageContext ??= createContext<AutoshipPercentageContextValue>(null);
  return globalContext.__petmAutoshipPercentageContext;
}
export const AutoshipPercentageContext = getAutoshipPercentageContext();
const initialPercentage = isLocaleCA() ? '15%' : '35%';
const activeSubscriptionPercentage = '5%';
const bannerPercentage = '5%';
export function AutoshipPercentageProvider(t0) {
  const $ = _c(15);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [itemPercentages, setItemPercentages] = useState(t1);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const {
    cart
  } = useCart();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getENV("NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED");
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const autoshipDiscountUpdatesEnabled = t2;
  const wasBannerDismissed = useRef(false);
  const {
    isAutoshipCrawl,
    isAutoshipPreCrawl
  } = useAutoshipDecisions();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (cartItems, percentage) => {
      setItemPercentages(prevState => applyPercentageToEligibleCartItemsState(prevState, cartItems, percentage));
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const applyPercentageToEligibleCartItems = t3;
  let t4;
  let t5;
  if ($[3] !== cart || $[4] !== isAutoshipCrawl || $[5] !== isAutoshipPreCrawl) {
    t4 = () => {
      if (!cart) {
        return;
      }
      const items = cart.fulfillmentGroups.flatMap(_temp2);
      if (autoshipDiscountUpdatesEnabled) {
        const hasAutoshipInCartValue = hasAutoshipInCart(cart);
        if (!hasAutoshipInCartValue) {
          setIsBannerVisible(false);
          wasBannerDismissed.current = false;
        }
        const hasAutoshipOrders = cart?.hasAutoshipOrders;
        if (hasAutoshipInCartValue && hasAutoshipOrders) {
          applyPercentageToEligibleCartItems(items, activeSubscriptionPercentage);
          if (cart?.id && !wasBannerDismissed.current) {
            setIsBannerVisible(true);
            wasBannerDismissed.current = true;
          }
          return;
        }
        if (hasAutoshipOrders) {
          applyPercentageToEligibleCartItems(items, activeSubscriptionPercentage);
          return;
        }
        if (isAutoshipCrawl || isAutoshipPreCrawl) {
          setItemPercentages(prevState_0 => mergeCrawlAutoshipPercentagesState(prevState_0, items));
        } else {
          applyPercentageToEligibleCartItems(items, initialPercentage);
        }
      } else {
        if (!cart?.hasAutoshipOrders) {
          applyPercentageToEligibleCartItems(items, initialPercentage);
        }
      }
    };
    t5 = [autoshipDiscountUpdatesEnabled, isAutoshipCrawl, isAutoshipPreCrawl, cart];
    $[3] = cart;
    $[4] = isAutoshipCrawl;
    $[5] = isAutoshipPreCrawl;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsBannerVisible(false);
      wasBannerDismissed.current = true;
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const hideBanner = t6;
  let t7;
  if ($[9] !== isBannerVisible || $[10] !== itemPercentages) {
    t7 = {
      bannerPercentage,
      itemPercentages,
      isBannerVisible,
      hideBanner
    };
    $[9] = isBannerVisible;
    $[10] = itemPercentages;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const contextValue = t7;
  let t8;
  if ($[12] !== children || $[13] !== contextValue) {
    t8 = <AutoshipPercentageContext.Provider value={contextValue}>{children}</AutoshipPercentageContext.Provider>;
    $[12] = children;
    $[13] = contextValue;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function _temp2(t0) {
  const {
    items: groupItems
  } = t0;
  return groupItems.map(_temp);
}
function _temp(cartItem) {
  return cartItem;
}